'use client'

import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  Plus,
  Webhook,
  Check,
  X,
  Edit,
  Trash2,
  TestTube,
  Activity,
  Globe,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react'
import api from '../../lib/api'
import { devicesQueryOptions } from '@/lib/queries'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { notify } from '@/lib/notify'

interface Device {
  id: string
  brand: string
  model: string
  enabled: boolean
  displayName?: string
}

interface WebhookDevice {
  _id: string
  brand: string
  deviceModel: string
  enabled: boolean
}

interface WebhookSubscription {
  _id: string
  user: string
  device?: WebhookDevice
  isActive: boolean
  events: string[]
  deliveryUrl: string
  signingSecret: string
  successfulDeliveryCount: number
  deliveryFailureCount: number
  deliveryAttemptCount: number
  lastDeliveryAttemptAt?: string
  lastDeliverySuccessAt?: string
  lastDeliveryFailureAt?: string
  createdAt: string
  updatedAt: string
}

const webhookSchema = z.object({
  deliveryUrl: z.string().url('Please enter a valid URL'),
  events: z.array(z.string()).min(1, 'Please select at least one event'),
  device: z.string().min(1, 'Please select a device'),
  isActive: z.boolean(),
})

type WebhookFormData = z.infer<typeof webhookSchema>

const eventOptions = [
  { value: 'MESSAGE_RECEIVED', label: 'Message Received', description: 'When a new SMS is received on your device' },
  { value: 'MESSAGE_SENT', label: 'Message Sent', description: 'When an SMS is successfully sent' },
  { value: 'MESSAGE_DELIVERED', label: 'Message Delivered', description: 'When an SMS is confirmed delivered' },
  { value: 'MESSAGE_FAILED', label: 'Message Failed', description: 'When an SMS fails to send' },
]

function CreateWebhookDialog({ onClose }: { onClose?: () => void }) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const form = useForm<WebhookFormData>({
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      deliveryUrl: '',
      events: ['MESSAGE_RECEIVED'],
      device: '',
      isActive: true,
    },
  })

  // Fetch devices for selection
  const { data: devices } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = form

  const selectedEvents = watch('events')

  const createWebhookMutation = useMutation({
    mutationFn: (data: WebhookFormData) => api.post('/webhooks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      notify.success('Webhook created')
      setOpen(false)
      reset()
      if (onClose) onClose()
    },
    onError: (error: any) => {
      notify.error("Couldn't create the webhook", error.response?.data?.message || error.message)
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Create Webhook
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" />
            Create New Webhook
          </DialogTitle>
          <DialogDescription>
            Set up a webhook to receive real-time notifications about SMS events
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit((data) => createWebhookMutation.mutate(data))} className="space-y-6 mt-4">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Webhook URL</label>
              <Input
                placeholder="https://your-domain.com/webhook"
                {...register('deliveryUrl')}
                className="w-full"
              />
              {errors.deliveryUrl && (
                <p className="text-sm text-destructive mt-1">{errors.deliveryUrl.message}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                The URL where webhook events will be sent
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Device</label>
              <Controller
                name="device"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger aria-label='Sender'>
                      <SelectValue placeholder="Select a specific device" />
                    </SelectTrigger>
                    <SelectContent>
                      {devices?.map((device: Device) => (
                        <SelectItem key={device.id} value={device.id} disabled={!device.enabled}>
                          <div className="flex items-center gap-2">
                            <span>{device.displayName || (device.brand && device.model ? `${device.brand} ${device.model}` : device.brand || device.model || 'Device')}</span>
                            {!device.enabled && (
                              <Badge variant="secondary" className="text-xs">Disabled</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.device && (
                <p className="text-sm text-destructive mt-1">{errors.device.message}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Select the specific device for this webhook
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Events to Subscribe</label>
              <div className="space-y-2">
                {eventOptions.map((event) => (
                  <div key={event.value} className="flex items-start space-x-3">
                    <Controller
                      name="events"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={field.value.includes(event.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field.onChange([...field.value, event.value])
                            } else {
                              field.onChange(field.value.filter((v) => v !== event.value))
                            }
                          }}
                          className="mt-1"
                        />
                      )}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{event.label}</div>
                      <div className="text-xs text-muted-foreground">{event.description}</div>
                    </div>
                  </div>
                ))}
              </div>
              {errors.events && (
                <p className="text-sm text-destructive mt-1">{errors.events.message}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Selected events: {selectedEvents?.length || 0}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createWebhookMutation.isPending}>
              {createWebhookMutation.isPending ? 'Creating...' : 'Create Webhook'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function WebhookCard({ webhook, onEdit, onDelete, onTest }: {
  webhook: WebhookSubscription
  onEdit: (webhook: WebhookSubscription) => void
  onDelete: (id: string) => void
  onTest: (id: string) => void
}) {
  const [showSecret, setShowSecret] = useState(false)

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = () => {
    if (!webhook.isActive) {
      return <Badge variant="secondary" className="gap-1"><XCircle className="h-3 w-3" />Disabled</Badge>
    }

    const totalAttempts = webhook.deliveryAttemptCount
    const successRate = totalAttempts > 0 ? (webhook.successfulDeliveryCount / totalAttempts) * 100 : 100

    if (totalAttempts === 0) {
      return <Badge variant="outline" className="gap-1"><AlertTriangle className="h-3 w-3" />No Deliveries</Badge>
    }

    if (successRate >= 95) {
      return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle2 className="h-3 w-3" />Healthy</Badge>
    } else if (successRate >= 80) {
      return <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-600"><AlertTriangle className="h-3 w-3" />Warning</Badge>
    } else {
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Failing</Badge>
    }
  }

  const copySecret = () => {
    navigator.clipboard.writeText(webhook.signingSecret)
    notify.success('Signing secret copied')
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <span className="truncate">{new URL(webhook.deliveryUrl).hostname}</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground truncate mt-1">
              {webhook.deliveryUrl}
            </p>
          </div>
          <div className="flex gap-2">
            {getStatusBadge()}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {webhook.events.map((event) => (
              <Badge key={event} variant="outline" className="text-xs">
                {eventOptions.find(e => e.value === event)?.label || event}
              </Badge>
            ))}
          </div>
          
          {webhook.device ? (
            <div className="text-sm">
              <span className="text-muted-foreground">Device: </span>
              <span className="font-medium">
                {webhook.device.brand} {webhook.device.deviceModel}
              </span>
              {!webhook.device.enabled && (
                <Badge variant="secondary" className="ml-2 text-xs">Disabled</Badge>
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Legacy webhook (no device specified)
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Success Rate</div>
            <div className="font-medium">
              {webhook.deliveryAttemptCount > 0 
                ? `${Math.round((webhook.successfulDeliveryCount / webhook.deliveryAttemptCount) * 100)}%`
                : '100%'
              } ({webhook.successfulDeliveryCount}/{webhook.deliveryAttemptCount})
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Last Delivery</div>
            <div className="font-medium">{formatDate(webhook.lastDeliveryAttemptAt)}</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Signing Secret</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-muted p-2 rounded text-xs font-mono">
              {showSecret ? webhook.signingSecret : '•'.repeat(32)}
            </code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSecret(!showSecret)}
              className="p-1"
            >
              {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={copySecret}
              className="p-1"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Use this secret to verify webhook authenticity
          </p>
        </div>

        <div className="flex justify-between items-center pt-2 border-t">
          <div className="text-xs text-muted-foreground">
            Created {formatDate(webhook.createdAt)}
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onTest(webhook._id)}
              className="gap-1 text-blue-600"
            >
              <TestTube className="h-3 w-3" />
              Test
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(webhook)}
              className="gap-1"
            >
              <Edit className="h-3 w-3" />
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(webhook._id)}
              className="gap-1 text-red-600"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default function Webhooks() {
  const queryClient = useQueryClient()

  const { data: webhooks, isLoading, error } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.get('/webhooks').then((res) => res.data?.data || []),
  })

  const deleteWebhookMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      notify.success('Webhook deleted')
    },
    onError: (error: any) => {
      notify.error("Couldn't delete the webhook", error.response?.data?.message || error.message)
    },
  })

  const testWebhookMutation = useMutation({
    mutationFn: (id: string) => api.post(`/queue/webhook/${id}/test`),
    onSuccess: () => {
      notify.success('Test sent', 'Check your endpoint for the test payload.')
    },
    onError: (error: any) => {
      notify.error("Couldn't send the test", error.response?.data?.message || error.message)
    },
  })

  const handleEdit = (webhook: WebhookSubscription) => {
    // For now, just show an alert. In a full implementation, you'd open an edit dialog
    notify.error('Editing is not available yet', 'Delete the webhook and create it again.')
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this webhook? This action cannot be undone.')) {
      deleteWebhookMutation.mutate(id)
    }
  }

  const handleTest = (id: string) => {
    testWebhookMutation.mutate(id)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-96 mt-2" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="h-48 w-full" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-destructive">Error Loading Webhooks</h3>
          <p className="text-muted-foreground">{(error as any).message}</p>
        </div>
        <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['webhooks'] })}>
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Webhook className="h-8 w-8" />
            Webhooks
          </h1>
          <p className="text-muted-foreground">
            Configure webhooks to receive real-time notifications about SMS events
          </p>
        </div>
        <CreateWebhookDialog />
      </div>

      {webhooks && webhooks.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent className="space-y-4">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
              <Webhook className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">No Webhooks Configured</h3>
              <p className="text-muted-foreground">
                Create your first webhook to start receiving real-time SMS notifications
              </p>
            </div>
            <CreateWebhookDialog />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {webhooks?.map((webhook: WebhookSubscription) => (
            <WebhookCard
              key={webhook._id}
              webhook={webhook}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTest={handleTest}
            />
          ))}
        </div>
      )}
    </div>
  )
}
